import React, { useState, useEffect, useCallback, useMemo, useRef } from "react";
import {
  Container,
  CssBaseline,
  ThemeProvider,
  createTheme,
  AppBar,
  Toolbar,
  Typography,
  Box,
  Tabs,
  Tab,
  ToggleButtonGroup,
  ToggleButton,
  TextField,
  MenuItem,
  InputAdornment,
  Snackbar,
  Alert,
} from "@mui/material";
import { CloudUpload, ViewList, ViewModule, Search } from "@mui/icons-material";
import { FileUpload } from "./components/FileUpload";
import { FileList } from "./components/FileList";
import { BucketId, FileMetadata, PaginationInfo, SortDir, SortField, SortState, UploadProgress } from "./types/File";
import { uploadService } from "./services/uploadService";
import { fileService } from "./services/fileService";
import { constructPublicUrl } from "./utils/urlUtils";
import { FileCategory, getDisplayFileName, getFileCategory } from "./utils/fileDisplay";

const theme = createTheme({
  palette: {
    mode: "dark",
    primary: {
      main: "#ff2d55", // Decentraland pink
      light: "#ff5d7a",
      dark: "#e0244a",
    },
    secondary: {
      main: "#00d4ff", // Decentraland cyan
      light: "#33ddff",
      dark: "#00a8cc",
    },
    background: {
      default: "#16141a", // Decentraland dark background
      paper: "#1e1e24", // Card/paper background
    },
    text: {
      primary: "#ffffff",
      secondary: "#b8b8b8",
    },
    divider: "rgba(255, 255, 255, 0.1)",
  },
  typography: {
    fontFamily: '"Inter", -apple-system, BlinkMacSystemFont, "Segoe UI", "Roboto", "Oxygen", "Ubuntu", "Cantarell", "Fira Sans", "Droid Sans", "Helvetica Neue", sans-serif',
    h4: {
      fontWeight: 600,
      fontSize: "1.75rem",
      lineHeight: 1.2,
    },
    h6: {
      fontWeight: 600,
      fontSize: "1.125rem",
    },
    body1: {
      fontSize: "0.9375rem",
      lineHeight: 1.5,
    },
    body2: {
      fontSize: "0.875rem",
      lineHeight: 1.5,
    },
    button: {
      fontWeight: 600,
      textTransform: "none",
      letterSpacing: "0.02em",
    },
  },
  shape: {
    borderRadius: 8,
  },
  components: {
    MuiAppBar: {
      styleOverrides: {
        root: {
          backgroundColor: "#1e1e24",
          boxShadow: "0 1px 3px rgba(0, 0, 0, 0.4)",
          borderBottom: "1px solid rgba(255, 255, 255, 0.1)",
        },
      },
    },
    MuiPaper: {
      styleOverrides: {
        root: {
          backgroundColor: "#1e1e24",
          border: "1px solid rgba(255, 255, 255, 0.1)",
          boxShadow: "0 2px 8px rgba(0, 0, 0, 0.3)",
        },
      },
    },
    MuiButton: {
      styleOverrides: {
        root: {
          borderRadius: 8,
          textTransform: "none",
          fontWeight: 600,
          padding: "10px 24px",
          fontSize: "0.9375rem",
        },
        contained: {
          boxShadow: "0 2px 8px rgba(0, 0, 0, 0.3)",
          "&:hover": {
            boxShadow: "0 4px 12px rgba(0, 0, 0, 0.4)",
          },
        },
      },
    },
    MuiChip: {
      styleOverrides: {
        root: {
          fontWeight: 500,
          fontSize: "0.8125rem",
        },
      },
    },
    MuiTableRow: {
      styleOverrides: {
        root: {
          "&:hover": {
            backgroundColor: "rgba(255, 255, 255, 0.03)",
          },
        },
      },
    },
    MuiTableCell: {
      styleOverrides: {
        root: {
          borderColor: "rgba(255, 255, 255, 0.1)",
        },
      },
    },
    MuiCard: {
      styleOverrides: {
        root: {
          backgroundColor: "#1e1e24",
          border: "1px solid rgba(255, 255, 255, 0.1)",
          boxShadow: "0 2px 8px rgba(0, 0, 0, 0.3)",
        },
      },
    },
    MuiPagination: {
      styleOverrides: {
        root: {
          "& .MuiPaginationItem-root": {
            color: "#b8b8b8",
            "&.Mui-selected": {
              backgroundColor: "#ff2d55",
              color: "#ffffff",
              "&:hover": {
                backgroundColor: "#e0244a",
              },
            },
            "&:hover": {
              backgroundColor: "rgba(255, 45, 85, 0.1)",
            },
          },
        },
      },
    },
    MuiAlert: {
      styleOverrides: {
        root: {
          borderRadius: 8,
          backgroundColor: "#1e1e24",
          border: "1px solid rgba(255, 255, 255, 0.1)",
        },
        standardWarning: {
          backgroundColor: "rgba(255, 193, 7, 0.1)",
          borderColor: "rgba(255, 193, 7, 0.3)",
          color: "#ffc107",
        },
        standardError: {
          backgroundColor: "rgba(244, 67, 54, 0.1)",
          borderColor: "rgba(244, 67, 54, 0.3)",
          color: "#f44336",
        },
        standardInfo: {
          backgroundColor: "rgba(0, 212, 255, 0.1)",
          borderColor: "rgba(0, 212, 255, 0.3)",
          color: "#00d4ff",
        },
        standardSuccess: {
          backgroundColor: "rgba(76, 175, 80, 0.1)",
          borderColor: "rgba(76, 175, 80, 0.3)",
          color: "#4caf50",
        },
      },
    },
    MuiDialog: {
      styleOverrides: {
        paper: {
          backgroundColor: "#1e1e24",
          border: "1px solid rgba(255, 255, 255, 0.1)",
        },
      },
    },
    MuiTextField: {
      styleOverrides: {
        root: {
          "& .MuiOutlinedInput-root": {
            backgroundColor: "#1e1e24",
            "& fieldset": {
              borderColor: "rgba(255, 255, 255, 0.1)",
            },
            "&:hover fieldset": {
              borderColor: "rgba(255, 255, 255, 0.2)",
            },
            "&.Mui-focused fieldset": {
              borderColor: "#00d4ff",
            },
          },
        },
      },
    },
  },
});

const FILES_PER_PAGE = 12;

const BUCKET_LABELS: Record<BucketId, string> = {
  marketing: "Marketing",
  external: "External",
};

const EMPTY_FILES: Record<BucketId, FileMetadata[]> = {
  marketing: [],
  external: [],
};

const DEFAULT_SORT: SortState = { field: "date", dir: "desc" };

type UploadOutcome =
  | { status: "success"; meta: FileMetadata }
  | { status: "error" }
  | { status: "cancelled" };

const PAGE_SIZE_OPTIONS = [12, 24, 48];
const PREFS_STORAGE_KEY = "fileManagerPrefs";

interface StoredPrefs {
  viewMode?: "table" | "grid";
  typeFilter?: FileCategory | "all";
  sort?: SortState;
  pageSize?: number;
}

function loadPrefs(): StoredPrefs {
  try {
    const raw = localStorage.getItem(PREFS_STORAGE_KEY);
    return raw ? (JSON.parse(raw) as StoredPrefs) : {};
  } catch {
    return {};
  }
}

function App() {
  // Which buckets the signed-in user may access (from GET /me) and which is active.
  const [availableBuckets, setAvailableBuckets] = useState<BucketId[]>(["marketing"]);
  const [selectedBucket, setSelectedBucket] = useState<BucketId>("marketing");
  // Files fetched per bucket (each already sorted by lastModified, newest first).
  const [filesByBucket, setFilesByBucket] = useState<Record<BucketId, FileMetadata[]>>(EMPTY_FILES);
  const [loadedBuckets, setLoadedBuckets] = useState<BucketId[]>([]);
  const [viewMode, setViewMode] = useState<"table" | "grid">(() => loadPrefs().viewMode || "table");
  const [loading, setLoading] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [searchQuery, setSearchQuery] = useState("");
  const [typeFilter, setTypeFilter] = useState<FileCategory | "all">(() => loadPrefs().typeFilter || "all");
  const [sort, setSort] = useState<SortState>(() => loadPrefs().sort || DEFAULT_SORT);
  const [pageSize, setPageSize] = useState<number>(() => loadPrefs().pageSize || FILES_PER_PAGE);
  const [uploadProgress, setUploadProgress] = useState<UploadProgress[]>([]);
  const [notification, setNotification] = useState<{
    open: boolean;
    message: string;
    severity: "success" | "error" | "info";
  }>({
    open: false,
    message: "",
    severity: "info",
  });
  const [apiAvailable, setApiAvailable] = useState(true);

  // Upload items keyed by a stable id so cancel/retry/remove never depend on array
  // position (which shifts when items are removed or a new batch starts mid-upload).
  const uploadItemsRef = useRef<Map<string, { file: File; controller: AbortController }>>(new Map());
  const uploadIdCounter = useRef(0);
  // Top of the search/filter + file-list section, so page changes scroll here rather than
  // all the way to the top of the page (which would hide the upload area).
  const listTopRef = useRef<HTMLDivElement>(null);

  // Persist view/filter/sort/page-size preferences across sessions.
  useEffect(() => {
    try {
      localStorage.setItem(
        PREFS_STORAGE_KEY,
        JSON.stringify({ viewMode, typeFilter, sort, pageSize })
      );
    } catch {
      // ignore storage errors (private mode, quota)
    }
  }, [viewMode, typeFilter, sort, pageSize]);

  // Search + type filter + sort applied to the active bucket's files.
  const filteredFiles = useMemo(() => {
    const bucketFiles = filesByBucket[selectedBucket] || [];
    const query = searchQuery.trim().toLowerCase();
    const dateValue = (f: FileMetadata) => (f.lastModified ? new Date(f.lastModified).getTime() : 0);

    const matched = bucketFiles.filter((file) => {
      const matchesSearch =
        !query || getDisplayFileName(file.key, file.filename).toLowerCase().includes(query);
      const matchesType = typeFilter === "all" || getFileCategory(file.type) === typeFilter;
      return matchesSearch && matchesType;
    });

    return matched.sort((a, b) => {
      let cmp: number;
      switch (sort.field) {
        case "name":
          cmp = getDisplayFileName(a.key, a.filename).localeCompare(
            getDisplayFileName(b.key, b.filename)
          );
          break;
        case "size":
          cmp = a.size - b.size;
          break;
        case "date":
        default:
          cmp = dateValue(a) - dateValue(b);
          break;
      }
      return sort.dir === "asc" ? cmp : -cmp;
    });
  }, [filesByBucket, selectedBucket, searchQuery, typeFilter, sort]);

  // Client-side pagination over the filtered/sorted list.
  const { files, pagination } = useMemo(() => {
    const startIndex = (currentPage - 1) * pageSize;
    const endIndex = startIndex + pageSize;

    return {
      files: filteredFiles.slice(startIndex, endIndex),
      pagination: {
        page: currentPage,
        limit: pageSize,
        total: filteredFiles.length,
        hasMore: endIndex < filteredFiles.length,
      } as PaginationInfo,
    };
  }, [filteredFiles, currentPage, pageSize]);

  // Keep the current page in range when the filtered set shrinks (e.g. after a delete)
  // or the page size grows, so we never strand the user on an empty out-of-range page.
  useEffect(() => {
    const totalPages = Math.max(1, Math.ceil(filteredFiles.length / pageSize));
    setCurrentPage((page) => (page > totalPages ? totalPages : page));
  }, [filteredFiles, pageSize]);

  const showNotification = useCallback(
    (message: string, severity: "success" | "error" | "info") => {
      setNotification({
        open: true,
        message,
        severity,
      });
    },
    []
  );

  /**
   * Generates mock file data for UI/UX testing when API is unavailable
   * @returns All mock files sorted by lastModified (newest first)
   */
  const generateMockFiles = useCallback((): FileMetadata[] => {
    const totalMockFiles = 25;
    const mockFiles: FileMetadata[] = [];
    
    // Generate files with dates from newest to oldest
    const now = new Date();
    for (let i = 0; i < totalMockFiles; i++) {
      const date = new Date(now);
      date.setDate(date.getDate() - i); // Each file is 1 day older
      date.setHours(10 + (i % 12), 30 + (i % 30), 0, 0);
      
      const fileTypes = [
        { type: "image/jpeg", name: "Art-Week_photo", ext: "jpg" },
        { type: "image/png", name: "Metadata_Image", ext: "png" },
        { type: "video/mp4", name: "Chess-dancing", ext: "mp4" },
        { type: "image/jpeg", name: "Digital-Fashion-Week-photo", ext: "jpg" },
        { type: "image/jpeg", name: "Hayabusa-photo", ext: "jpg" },
      ];
      const fileType = fileTypes[i % fileTypes.length];
      const size = fileType.type.startsWith("video/") 
        ? 37.95 * 1024 * 1024 // ~38MB for videos
        : (8.63 + Math.random() * 5) * 1024 * 1024; // 8-13MB for images
      
      const mockKey = `uploads/${Date.now() - i * 86400000}_${fileType.name}.${fileType.ext}`;
      mockFiles.push({
        key: mockKey,
        url: constructPublicUrl(mockKey, "marketing"),
        size: Math.round(size),
        type: fileType.type,
        lastModified: date.toISOString(),
        filename: `${fileType.name}.${fileType.ext}`,
      });
    }
    
    return mockFiles;
  }, []);

  // Load all files for a bucket (fetches everything and stores it sorted by lastModified).
  const loadBucket = useCallback(
    async (bucket: BucketId) => {
      setLoading(true);
      try {
        const files = await fileService.getAllFiles(bucket, "uploads/");
        setFilesByBucket((prev) => ({ ...prev, [bucket]: files }));
        setLoadedBuckets((prev) => (prev.includes(bucket) ? prev : [...prev, bucket]));
        setApiAvailable(true);
      } catch (error) {
        console.error(`Error loading files for ${bucket}:`, error);
        showNotification("Failed to load files. Please try again.", "error");
      } finally {
        setLoading(false);
      }
    },
    [showNotification]
  );

  // On mount: discover which buckets the user may access, then load the first one.
  const initialize = useCallback(async () => {
    setLoading(true);
    try {
      const me = await fileService.getMe();
      const buckets: BucketId[] = me.buckets && me.buckets.length > 0 ? me.buckets : ["marketing"];
      const initial = buckets[0];
      setAvailableBuckets(buckets);
      setSelectedBucket(initial);
      setCurrentPage(1);
      setApiAvailable(true);
      await loadBucket(initial);
    } catch (error) {
      // No identity available (e.g. local dev without Cloudflare Access, or API down):
      // fall back to sample data so the UI is still explorable.
      console.error("Error loading identity:", error);
      setAvailableBuckets(["marketing"]);
      setSelectedBucket("marketing");
      setCurrentPage(1);
      setApiAvailable(false);
      const mockFiles = generateMockFiles();
      setFilesByBucket((prev) => ({ ...prev, marketing: mockFiles }));
      setLoadedBuckets(["marketing"]);
      setLoading(false);
    }
  }, [loadBucket, generateMockFiles]);

  // Run once on mount
  useEffect(() => {
    initialize();
  }, [initialize]);

  const handleCloseNotification = () => {
    setNotification((prev) => ({ ...prev, open: false }));
  };

  const handleBucketChange = (
    _event: React.SyntheticEvent,
    newBucket: BucketId
  ) => {
    setSelectedBucket(newBucket);
    setCurrentPage(1);
    if (!loadedBuckets.includes(newBucket)) {
      loadBucket(newBucket);
    }
  };

  // Uploads a single file identified by a stable id, tracking progress and supporting
  // cancellation via a per-file AbortController.
  const uploadOne = useCallback(
    async (id: string, file: File): Promise<UploadOutcome> => {
      const controller = new AbortController();
      uploadItemsRef.current.set(id, { file, controller });
      const patch = (changes: Partial<UploadProgress>) =>
        setUploadProgress((prev) =>
          prev.map((item) => (item.id === id ? { ...item, ...changes } : item))
        );
      patch({ progress: 0, status: "uploading" });

      try {
        const meta = await uploadService.uploadFile(
          file,
          selectedBucket,
          (progress) => patch({ progress, status: progress === 100 ? "completed" : "uploading" }),
          controller.signal
        );
        patch({ progress: 100, status: "completed" });
        return { status: "success", meta };
      } catch (error) {
        const aborted =
          controller.signal.aborted || (error instanceof Error && error.name === "AbortError");
        patch({ status: aborted ? "cancelled" : "error" });
        return { status: aborted ? "cancelled" : "error" };
      }
    },
    [selectedBucket]
  );

  // Optimistically show freshly uploaded files, then reconcile against the server.
  const reconcileAfterUpload = useCallback(
    async (metas: FileMetadata[]) => {
      if (metas.length > 0) {
        setFilesByBucket((prev) => ({
          ...prev,
          [selectedBucket]: [...metas, ...(prev[selectedBucket] || [])],
        }));
        setCurrentPage(1);
      }
      await loadBucket(selectedBucket);
    },
    [selectedBucket, loadBucket]
  );

  const scheduleProgressCleanup = useCallback(() => {
    setTimeout(() => {
      setUploadProgress((prev) => {
        if (prev.some((item) => item.status === "uploading")) return prev;
        uploadItemsRef.current.clear();
        return [];
      });
    }, 2000);
  }, []);

  const handleFilesUploaded = useCallback(
    async (uploadedFiles: File[]) => {
      const items = uploadedFiles.map((file) => ({
        id: String(++uploadIdCounter.current),
        file,
      }));
      // Append rather than replace, so a paste or second drop mid-upload doesn't disturb
      // in-flight items.
      setUploadProgress((prev) => [
        ...prev,
        ...items.map(({ id, file }) => ({
          id,
          fileName: file.name,
          progress: 0,
          status: "uploading" as const,
        })),
      ]);

      const outcomes = await Promise.all(items.map(({ id, file }) => uploadOne(id, file)));
      const metas = outcomes.flatMap((o) => (o.status === "success" ? [o.meta] : []));
      const cancelled = outcomes.filter((o) => o.status === "cancelled").length;
      const failed = outcomes.filter((o) => o.status === "error").length;

      await reconcileAfterUpload(metas);

      if (failed === 0 && cancelled === 0) {
        showNotification(
          `Successfully uploaded ${metas.length} file${metas.length !== 1 ? "s" : ""}`,
          "success"
        );
      } else {
        const parts = [`${metas.length} of ${uploadedFiles.length} uploaded`];
        if (failed) parts.push(`${failed} failed`);
        if (cancelled) parts.push(`${cancelled} cancelled`);
        showNotification(parts.join(" — "), metas.length === 0 ? "error" : "info");
      }

      scheduleProgressCleanup();
    },
    [uploadOne, reconcileAfterUpload, scheduleProgressCleanup, showNotification]
  );

  const handleCancelUpload = (id: string) => {
    uploadItemsRef.current.get(id)?.controller.abort();
  };

  const handleRetryUpload = async (id: string) => {
    const entry = uploadItemsRef.current.get(id);
    if (!entry) return;
    const outcome = await uploadOne(id, entry.file);
    if (outcome.status === "success") {
      await reconcileAfterUpload([outcome.meta]);
      showNotification("File uploaded", "success");
    } else if (outcome.status === "error") {
      showNotification("Upload failed. Please try again.", "error");
    }
    scheduleProgressCleanup();
  };

  // Paste-to-upload: images pasted from the clipboard upload to the active bucket,
  // unless the user is typing in a field.
  useEffect(() => {
    const onPaste = (event: ClipboardEvent) => {
      const target = event.target as HTMLElement | null;
      if (
        target &&
        (target.tagName === "INPUT" || target.tagName === "TEXTAREA" || target.isContentEditable)
      ) {
        return;
      }
      const files = Array.from(event.clipboardData?.files || []);
      if (files.length > 0) {
        handleFilesUploaded(files);
      }
    };
    document.addEventListener("paste", onPaste);
    return () => document.removeEventListener("paste", onPaste);
  }, [handleFilesUploaded]);

  const handleViewModeChange = (
    event: React.MouseEvent<HTMLElement>,
    newViewMode: "table" | "grid"
  ) => {
    if (newViewMode !== null) {
      setViewMode(newViewMode);
    }
  };

  const handlePageChange = (page: number) => {
    // Client-side pagination over the filtered/sorted list
    const totalPages = Math.ceil(filteredFiles.length / pageSize);
    const validPage = Math.max(1, Math.min(page, totalPages || 1));
    setCurrentPage(validPage);
    // Bring the top of the list back into view (not the whole page), so the new page
    // starts at the top of the viewport with the upload area just above it.
    listTopRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  // Toggle sort from a table column header: same field flips direction, a new field
  // starts with a sensible default (A–Z for name, newest/largest first otherwise).
  const handleSort = useCallback((field: SortField) => {
    setSort((prev) =>
      prev.field === field
        ? { field, dir: prev.dir === "asc" ? "desc" : "asc" }
        : { field, dir: field === "name" ? "asc" : "desc" }
    );
    setCurrentPage(1);
  }, []);

  const handleFileDeleted = () => {
    // Reload the active bucket after a deletion; the list component owns the toast.
    loadBucket(selectedBucket);
  };

  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      <AppBar position="static" elevation={0}>
        <Toolbar>
          <Box sx={{ display: "flex", alignItems: "center", mr: 2 }}>
            <img
              src="/decentraland-logo.png"
              alt="Decentraland"
              style={{
                height: 32,
                width: "auto",
                marginRight: 12,
              }}
            />
            <CloudUpload sx={{ mr: 1 }} />
          </Box>
          <Typography variant="h6" component="div" sx={{ flexGrow: 1 }}>
            File Manager
          </Typography>
          <ToggleButtonGroup
            value={viewMode}
            exclusive
            onChange={handleViewModeChange}
            size="small"
            sx={{ mr: 2 }}
          >
            <ToggleButton value="table" aria-label="table view">
              <ViewList />
            </ToggleButton>
            <ToggleButton value="grid" aria-label="grid view">
              <ViewModule />
            </ToggleButton>
          </ToggleButtonGroup>
        </Toolbar>
      </AppBar>

      {availableBuckets.length > 1 && (
        <Box
          sx={{
            borderBottom: "1px solid rgba(255, 255, 255, 0.1)",
            backgroundColor: "#1e1e24",
            px: 2,
          }}
        >
          <Container maxWidth="lg" disableGutters>
            <Tabs
              value={selectedBucket}
              onChange={handleBucketChange}
              textColor="inherit"
              indicatorColor="primary"
            >
              {availableBuckets.map((bucket) => (
                <Tab key={bucket} value={bucket} label={BUCKET_LABELS[bucket]} />
              ))}
            </Tabs>
          </Container>
        </Box>
      )}

      <Container maxWidth="lg" sx={{ py: 4 }}>
        <Typography variant="h4" gutterBottom>
          Upload & Manage Your Assets
        </Typography>
        <Typography variant="body1" color="text.secondary" sx={{ mb: 4 }}>
          Drag and drop files to upload them to your CDN. Copy URLs with one
          click to share them.
        </Typography>

        {!apiAvailable && (
          <Alert severity="warning" sx={{ mb: 3 }}>
            <Typography variant="body2">
              <strong>Showing sample data.</strong> The file management server is
              unavailable, or you are running locally without access. Uploading and
              managing real files requires signing in through the deployed site.
            </Typography>
          </Alert>
        )}

        <FileUpload
          onFilesUploaded={handleFilesUploaded}
          uploadProgress={uploadProgress}
          onClearProgress={() => {
            uploadItemsRef.current.clear();
            setUploadProgress([]);
          }}
          onRemoveProgressItem={(id) => {
            uploadItemsRef.current.delete(id);
            setUploadProgress((prev) => prev.filter((item) => item.id !== id));
          }}
          onCancelUpload={handleCancelUpload}
          onRetryUpload={handleRetryUpload}
        />

        <Box sx={{ mt: 4 }} ref={listTopRef}>
          {(filesByBucket[selectedBucket]?.length ?? 0) > 0 && (
            <Box
              sx={{
                display: "flex",
                gap: 2,
                mb: 2,
                flexWrap: "wrap",
                alignItems: "center",
              }}
            >
              <TextField
                size="small"
                placeholder="Search files…"
                value={searchQuery}
                onChange={(e) => {
                  setSearchQuery(e.target.value);
                  setCurrentPage(1);
                }}
                sx={{ flexGrow: 1, minWidth: 220 }}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <Search fontSize="small" />
                    </InputAdornment>
                  ),
                }}
              />
              <TextField
                select
                size="small"
                label="Type"
                value={typeFilter}
                onChange={(e) => {
                  setTypeFilter(e.target.value as FileCategory | "all");
                  setCurrentPage(1);
                }}
                sx={{ minWidth: 140 }}
              >
                <MenuItem value="all">All types</MenuItem>
                <MenuItem value="image">Images</MenuItem>
                <MenuItem value="video">Videos</MenuItem>
                <MenuItem value="audio">Audio</MenuItem>
                <MenuItem value="document">Documents</MenuItem>
                <MenuItem value="other">Other</MenuItem>
              </TextField>
              <TextField
                select
                size="small"
                label="Sort"
                value={`${sort.field}-${sort.dir}`}
                onChange={(e) => {
                  const [field, dir] = e.target.value.split("-") as [SortField, SortDir];
                  setSort({ field, dir });
                  setCurrentPage(1);
                }}
                sx={{ minWidth: 160 }}
              >
                <MenuItem value="date-desc">Newest first</MenuItem>
                <MenuItem value="date-asc">Oldest first</MenuItem>
                <MenuItem value="name-asc">Name (A–Z)</MenuItem>
                <MenuItem value="name-desc">Name (Z–A)</MenuItem>
                <MenuItem value="size-desc">Largest first</MenuItem>
                <MenuItem value="size-asc">Smallest first</MenuItem>
              </TextField>
              <TextField
                select
                size="small"
                label="Per page"
                value={pageSize}
                onChange={(e) => {
                  setPageSize(Number(e.target.value));
                  setCurrentPage(1);
                }}
                sx={{ minWidth: 110 }}
              >
                {PAGE_SIZE_OPTIONS.map((size) => (
                  <MenuItem key={size} value={size}>
                    {size} / page
                  </MenuItem>
                ))}
              </TextField>
            </Box>
          )}
          <FileList
            files={files}
            pagination={pagination}
            viewMode={viewMode}
            loading={loading}
            bucket={selectedBucket}
            filtered={searchQuery.trim() !== "" || typeFilter !== "all"}
            sort={sort}
            onSort={handleSort}
            onPageChange={handlePageChange}
            onFileDeleted={handleFileDeleted}
          />
        </Box>
      </Container>

      {/* Toast Notifications */}
      <Snackbar
        open={notification.open}
        autoHideDuration={4000}
        onClose={handleCloseNotification}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
      >
        <Alert
          onClose={handleCloseNotification}
          severity={notification.severity}
          variant="filled"
          sx={{ width: "100%" }}
        >
          {notification.message}
        </Alert>
      </Snackbar>
    </ThemeProvider>
  );
}

export default App;
